import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { listTasks } from '@/api/tasks';
import TaskFilterStrip from '@/components/tasks/TaskFilterStrip';
import TasksTable from '@/components/tasks/TasksTable';
import { Button } from '@/components/ui/button';
import type { CallStatus, TaskListQuery } from '@/types/api';

function paramsToQuery(params: URLSearchParams): TaskListQuery {
  return {
    status: params.getAll('status') as CallStatus[],
    phone: params.get('phone') ?? undefined,
    started_from: params.get('started_from') ?? undefined,
    started_to: params.get('started_to') ?? undefined,
    direction: (params.get('direction') as '1' | '2' | null) ?? undefined,
    order:
      (params.get('order') as TaskListQuery['order']) ?? 'started_desc',
    limit: Number(params.get('limit') ?? '50'),
    cursor: params.get('cursor') ?? undefined,
  };
}

export default function TasksListPage() {
  const { t } = useTranslation('tasks');
  const [params, setParams] = useSearchParams();
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const query = paramsToQuery(params);
  const tasksQuery = useQuery({
    queryKey: ['tasks', 'list', Object.fromEntries(params)],
    queryFn: () => listTasks(query),
  });

  function goNext() {
    const next = tasksQuery.data?.next_cursor;
    if (!next) return;
    setCursorStack((s) => [...s, params.get('cursor') ?? '']);
    const np = new URLSearchParams(params);
    np.set('cursor', next);
    setParams(np, { replace: false });
  }

  function goPrev() {
    if (cursorStack.length === 0) return;
    const last = cursorStack[cursorStack.length - 1] ?? '';
    setCursorStack((s) => s.slice(0, -1));
    const np = new URLSearchParams(params);
    if (last) np.set('cursor', last);
    else np.delete('cursor');
    setParams(np, { replace: false });
  }

  const items = tasksQuery.data?.items ?? [];

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-2xl font-semibold">{t('title')}</h2>
      <TaskFilterStrip />
      <div className="text-xs text-muted-foreground">
        {t('filters.results', { n: items.length })}
      </div>
      <TasksTable items={items} loading={tasksQuery.isLoading} />
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={goPrev}
          disabled={cursorStack.length === 0}
        >
          {t('pagination.prev')}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={goNext}
          disabled={!tasksQuery.data?.next_cursor}
        >
          {t('pagination.next')}
        </Button>
      </div>
    </div>
  );
}
